import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  Mail, 
  Key, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Eye,
  Edit,
  Settings,
  Bell,
  BarChart3,
  Calendar,
  MapPin,
  Phone,
  Globe,
  Upload,
  Plus
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { apiService } from "@/services/api";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

interface OperatorData {
  id: string;
  name: string;
  email: string;
  phone?: string;
  website?: string;
  status: 'pending' | 'approved' | 'rejected';
  plan: 'free' | 'basic' | 'premium';
  location?: {
    id: string;
    name: string;
    address: string;
    city: string;
    description: string;
    images: string[];
    services: string[];
  };
  stats?: {
    profileViews: number;
    contactClicks: number;
    bookingRequests: number;
    conversionRate: number;
  };
  notifications?: Array<{
    id: string;
    type: 'status_change' | 'approval' | 'feedback' | 'system';
    title: string;
    message: string;
    read: boolean;
    createdAt: string;
  }>;
}

const OperatorPortal: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const { toast } = useToast();
  
  const [operatorData, setOperatorData] = useState<OperatorData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimCode, setClaimCode] = useState(searchParams.get('code') || '');
  const [claimEmail, setClaimEmail] = useState('');
  const [isClaiming, setIsClaiming] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Check if user is already an operator
  const isOperator = user?.role === 'operator' || user?.roles?.includes('operator');
  const isAdmin = user?.role === 'admin' || user?.roles?.includes('admin');

  useEffect(() => {
    if (isOperator && !loading) {
      fetchOperatorData();
    }
  }, [isOperator, loading]);

  const fetchOperatorData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      // Fetch operator data from API
      const data = await apiService.getOperatorProfile();
      // Fetch notifications
      let notifications = [];
      try {
        notifications = await apiService.getOperatorNotifications();
      } catch (notifErr) {
        // Notifications are optional, don't block UI
        notifications = [];
      }
      setOperatorData({ ...data, notifications });
    } catch (err: any) {
      setError(err.message || 'Failed to load operator data');
      console.error('Error fetching operator data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPreviewData = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const data = await apiService.getOperatorPreview();
      setPreviewData(data);
    } catch (err: any) {
      setPreviewError(err.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Fetch preview data when operatorData is loaded and preview tab is selected
  useEffect(() => {
    if (isOperator && operatorData) {
      fetchPreviewData();
    }
    // eslint-disable-next-line
  }, [isOperator, operatorData]);

  const handleClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!claimEmail.trim()) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }
    try {
      setIsClaiming(true);
      // Call claim API
      await apiService.claimOperatorListing({
        email: claimEmail,
        code: claimCode || undefined
      });
      toast({
        title: "Claim request sent",
        description: "We've sent you an email with next steps to claim your listing.",
      });
      setClaimEmail('');
      setClaimCode('');
    } catch (err: any) {
      toast({
        title: "Claim failed",
        description: err.message || "Failed to submit claim request",
        variant: "destructive",
      });
    } finally {
      setIsClaiming(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><AlertCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'premium':
        return <Badge className="bg-purple-100 text-purple-800">Premium</Badge>;
      case 'basic':
        return <Badge className="bg-blue-100 text-blue-800">Basic</Badge>;
      case 'free':
        return <Badge className="bg-gray-100 text-gray-800">Free</Badge>;
      default:
        return <Badge variant="secondary">{plan}</Badge>;
    }
  };

  if (loading || isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner size="xl" variant="default" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // If user is admin, redirect to admin dashboard
  if (isAdmin) {
    navigate('/admin');
    return null;
  }

  // If user is operator, show dashboard
  if (isOperator && operatorData) {
    return (
      <>
        <Helmet>
          <title>Operator Portal | SaunaDip</title>
          <meta name="description" content="Manage your sauna business listing on SaunaDip" />
        </Helmet>
        <TooltipProvider>
        <div className="bg-gray-50 min-h-screen py-8 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Welcome, {operatorData.name}
                </h1>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-gray-500">Operator Portal</span>
                  {getStatusBadge(operatorData.status)}
                  {getPlanBadge(operatorData.plan)}
                </div>
              </div>
              
              <div className="flex gap-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button asChild variant="outline" aria-label="View Public Listing">
                      <a href={`/sauna/${operatorData.location?.id || '14'}`} target="_blank" rel="noopener noreferrer">
                        <Eye className="w-4 h-4 mr-2" />
                        View Public Listing
                      </a>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>See how your listing appears to customers</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button asChild aria-label="Edit Listing">
                      <a href="/operator/edit">
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Listing
                      </a>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit your business details, services, and images</TooltipContent>
                </Tooltip>
              </div>
            </div>

            {/* Main Content */}
            <Tabs defaultValue="dashboard" className="space-y-6">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
              </TabsList>

              <TabsContent value="dashboard" className="space-y-6">
                {/* Stats Cards */}
                {operatorData.stats && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Profile Views</CardTitle>
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{operatorData.stats.profileViews}</div>
                        <p className="text-xs text-muted-foreground">+12% from last month</p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Contact Clicks</CardTitle>
                        <Phone className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{operatorData.stats.contactClicks}</div>
                        <p className="text-xs text-muted-foreground">+8% from last month</p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Booking Requests</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{operatorData.stats.bookingRequests}</div>
                        <p className="text-xs text-muted-foreground">-3% from last month</p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                        <BarChart3 className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{operatorData.stats.conversionRate}%</div>
                        <p className="text-xs text-muted-foreground">+1.5% from last month</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button asChild variant="outline" className="h-20 flex-col gap-1" aria-label="Edit Profile">
                            <a href="/operator/edit">
                              <Edit className="w-6 h-6" />
                              <span>Edit Profile</span>
                            </a>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit your business details</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button asChild variant="outline" className="h-20 flex-col gap-1" aria-label="Manage Services">
                            <a href="/operator/services">
                              <Settings className="w-6 h-6" />
                              <span>Manage Services</span>
                            </a>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Update your offered services</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button asChild variant="outline" className="h-20 flex-col gap-1" aria-label="Upload Photos">
                            <a href="/operator/photos">
                              <Upload className="w-6 h-6" />
                              <span>Upload Photos</span>
                            </a>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Add or update your business images</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button asChild variant="outline" className="h-20 flex-col gap-1" aria-label="Upgrade Plan">
                            <a href="/operator/subscription">
                              <BarChart3 className="w-6 h-6" />
                              <span>Upgrade Plan</span>
                            </a>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>See available plans and upgrade</TooltipContent>
                      </Tooltip>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="profile" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Business Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Business Name</Label>
                        <p className="text-sm text-gray-600">{operatorData.location?.name}</p>
                      </div>
                      <div>
                        <Label>Contact Email</Label>
                        <p className="text-sm text-gray-600">{operatorData.email}</p>
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <p className="text-sm text-gray-600">{operatorData.phone || 'Not provided'}</p>
                      </div>
                      <div>
                        <Label>Website</Label>
                        <p className="text-sm text-gray-600">
                          {operatorData.website ? (
                            <a href={operatorData.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {operatorData.website}
                            </a>
                          ) : 'Not provided'}
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Address</Label>
                      <p className="text-sm text-gray-600">{operatorData.location?.address}</p>
                    </div>
                    
                    <div>
                      <Label>Description</Label>
                      <p className="text-sm text-gray-600">{operatorData.location?.description}</p>
                    </div>
                    
                    <div>
                      <Label>Services</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {operatorData.location?.services.map((service, index) => (
                          <Badge key={index} variant="secondary">{service}</Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="preview" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Public Listing Preview</CardTitle>
                    <p className="text-sm text-gray-600">This is how your listing appears to customers</p>
                  </CardHeader>
                  <CardContent>
                    {previewLoading ? (
                      <div className="flex justify-center items-center h-32">
                        <LoadingSpinner size="lg" variant="default" />
                      </div>
                    ) : previewError ? (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          {previewError}
                        </AlertDescription>
                      </Alert>
                    ) : previewData ? (
                      <div className="border rounded-lg p-6 bg-white">
                        <div className="flex items-start gap-4">
                          {previewData.images && previewData.images[0] && (
                            <img 
                              src={previewData.images[0]} 
                              alt={previewData.name}
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                          )}
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold">{previewData.name}</h3>
                            <p className="text-gray-600 text-sm mb-2">{previewData.address}</p>
                            <p className="text-gray-700 mb-3">{previewData.description}</p>
                            <div className="flex flex-wrap gap-2">
                              {previewData.services && previewData.services.slice(0, 3).map((service: string, index: number) => (
                                <Badge key={index} variant="outline">{service}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          No preview data available. Please complete your profile setup.
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="notifications" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Notifications</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {operatorData.notifications && operatorData.notifications.length > 0 ? (
                      <div className="space-y-4">
                        {operatorData.notifications.map((notification) => (
                          <div 
                            key={notification.id} 
                            className={`p-4 border rounded-lg ${notification.read ? 'bg-gray-50' : 'bg-blue-50 border-blue-200'}`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium">{notification.title}</h4>
                                <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                                <p className="text-xs text-gray-500 mt-2">
                                  {new Date(notification.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                              {!notification.read && (
                                <Badge className="bg-blue-100 text-blue-800">New</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No notifications yet</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
        </TooltipProvider>
      </>
    );
  }

  // Show claim form for non-operators
  return (
    <>
      <Helmet>
        <title>Claim Your Listing | SaunaDip</title>
        <meta name="description" content="Claim and manage your sauna business listing on SaunaDip" />
      </Helmet>
      
      <div className="bg-gray-50 min-h-screen py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <Building2 className="w-16 h-16 text-sauna-blue mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Claim Your Business Listing
            </h1>
            <p className="text-gray-600">
              Access your business dashboard to manage your listing, view analytics, and connect with customers.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Claim Your Listing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleClaim} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your business email"
                    value={claimEmail}
                    onChange={(e) => setClaimEmail(e.target.value)}
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    We'll send you a link to claim your listing
                  </p>
                </div>

                {claimCode && (
                  <div>
                    <Label htmlFor="code">Claim Code (Optional)</Label>
                    <Input
                      id="code"
                      type="text"
                      placeholder="Enter claim code if you have one"
                      value={claimCode}
                      onChange={(e) => setClaimCode(e.target.value)}
                    />
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isClaiming}>
                  {isClaiming ? (
                    <>
                      <LoadingSpinner size="sm" variant="default" className="mr-2" />
                      Sending Claim Request...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Claim Request
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t">
                <h3 className="font-medium mb-3">Already have an account?</h3>
                <Button asChild variant="outline" className="w-full">
                  <a href="/login">Sign In</a>
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Eye className="w-8 h-8 text-sauna-blue mx-auto mb-2" />
                  <h3 className="font-medium">View Analytics</h3>
                  <p className="text-sm text-gray-600">Track profile views and customer engagement</p>
                  <Button 
                    asChild 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                  >
                    <a href="/operator-analytics">View Analytics</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Edit className="w-8 h-8 text-sauna-blue mx-auto mb-2" />
                  <h3 className="font-medium">Update Information</h3>
                  <p className="text-sm text-gray-600">Keep your business details current</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Bell className="w-8 h-8 text-sauna-blue mx-auto mb-2" />
                  <h3 className="font-medium">Get Notifications</h3>
                  <p className="text-sm text-gray-600">Stay informed about your listing status</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default OperatorPortal; 